import axios from 'axios';

/**
 * Admin API client.
 *
 * The admin token has audience `grihvaas:admin` and is rejected by the public
 * API, so there is no risk of it leaking marketplace access.
 *
 * Kept in localStorage. This was sessionStorage — a back-office session dying
 * with the tab is the safer default — but the panel has no refresh flow, so
 * that plus a short token meant signing in again several times a day. The
 * session now lasts as long as the token does (`ADMIN_SESSION_TTL`, 7d), and
 * `signOut` and a 401 both clear it.
 */

const TOKEN_KEY = 'grihvaas.admin.token';
const ADMIN_KEY = 'grihvaas.admin.profile';

const storage = localStorage;

export const client = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/v1/admin`,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

export function getToken() {
  try {
    return storage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSession(token, admin) {
  try {
    if (token) {
      storage.setItem(TOKEN_KEY, token);
      storage.setItem(ADMIN_KEY, JSON.stringify(admin));
    } else {
      storage.removeItem(TOKEN_KEY);
      storage.removeItem(ADMIN_KEY);
    }
  } catch {
    // A browser with storage disabled still works for the current page load.
  }
}

export function getStoredAdmin() {
  try {
    const raw = storage.getItem(ADMIN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

let onUnauthorized = null;
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    // The admin API has no refresh flow by design — a back-office session that
    // has expired should be re-established deliberately.
    if (error.response?.status === 401) {
      setSession(null, null);
      onUnauthorized?.();
    }
    return Promise.reject(normalizeError(error));
  },
);

/**
 * Normalises a failure into something renderable.
 * The API already refuses to send internals, so the server's message is safe
 * to show — but a network failure has no server message at all.
 */
export function normalizeError(error) {
  if (!error.response) {
    return {
      code: 'network_error',
      message: 'Could not reach the API. Check that the server is running.',
      status: 0,
    };
  }

  const body = error.response.data?.error;
  return {
    code: body?.code || 'unknown_error',
    message: body?.message || 'Something went wrong.',
    details: body?.details || null,
    status: error.response.status,
    requestId: error.response.data?.requestId,
  };
}

export function unwrap(response) {
  return response.data?.data;
}

export function unwrapPage(response) {
  return { items: response.data?.data || [], meta: response.data?.meta || {} };
}

export default client;
